import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'

import useAuthStore from '../../../stores/useAuthStore/useAuthStore'

import {
    BenchmarkingRequestParams,
    BenchmarkingResponse,
    GetInvoicesSummaryResponseBody,
    InvoicesSummaryRequestParams,
    UsageCostRequestParams,
    UsageCostResponseBody,
} from '../types'
import { processUsageCostResponse } from '../utils/processUsageCostResponse'
import { useAxiosInstance } from '../useAxiosInstance'
import dayjs from 'dayjs'

export const useAnalyticsApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)

    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''

    const useUsageCostQuery = (
        params?: UsageCostRequestParams,
        opts: useUsageCostQueryOpts = { enabled: true }
    ) => {
        const query = useQuery({
            queryKey: ['usage-cost', customerId, params, accessToken],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<UsageCostResponseBody>(
                            `/api/v1/customers/${customerId}/reports/usage-cost`,
                            {
                                params,
                            }
                        )

                    const processedResponseData = processUsageCostResponse(
                        response.data
                    )

                    opts.onSuccess?.(processedResponseData)
                    return processUsageCostResponse(processedResponseData)
                } catch (error) {
                    opts.onError?.()
                    throw error
                }
            },
            enabled:
                opts.enabled &&
                !!accessToken &&
                !!customerId &&
                !!params?.['nodeIds[]'] &&
                !!params.commodityTypeId,
            placeholderData: (prev) => prev,
        })

        const downloadXlsx = useCallback(
            async (fields: string[]) => {
                const response = await axiosInstance.get<Blob>(
                    `/api/v1/customers/${customerId}/reports/usage-cost`,
                    {
                        params: {
                            ...params,
                            ...(fields.length > 0
                                ? { 'fields[]': fields.join(',') }
                                : {}),
                        },
                        headers: {
                            Accept: 'application/vnd.ms-excel',
                        },
                        responseType: 'blob',
                    }
                )

                // Create a download link
                const url = window.URL.createObjectURL(
                    new Blob([response.data])
                )
                const link = document.createElement('a')
                link.href = url
                link.setAttribute(
                    'download',
                    `usage-and-cost-${dayjs().format('MM-DD-YYYY')}.xlsx`
                )
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            },
            [params]
        )

        return { ...query, downloadXlsx }
    }
    const useInvoicesSummaryQuery = (params?: InvoicesSummaryRequestParams) => {
        return useQuery({
            queryKey: ['invoices-summary', customerId, accessToken, params],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetInvoicesSummaryResponseBody>(
                        `/api/v1/customers/${customerId}/invoices/summary`,
                        { params }
                    )
                return response.data
            },
            enabled: !!accessToken && !!customerId,
            placeholderData: (prev) => prev,
        })
    }

    const useBenchmarkingQuery = (
        params?: BenchmarkingRequestParams,
        options: useBenchmarkingQueryOpts = { enabled: true }
    ) => {
        const sanitizedParams = params

        if (!sanitizedParams?.applySquareFootage) {
            delete sanitizedParams?.applySquareFootage
        }

        if (!sanitizedParams?.applyWeatherNormalization) {
            delete sanitizedParams?.applyWeatherNormalization
        }

        if (!sanitizedParams?.includeEmissions) {
            delete sanitizedParams?.includeEmissions
        }

        const query = useQuery({
            queryKey: [
                'benchmarking',
                customerId,
                accessToken,
                sanitizedParams,
            ],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<BenchmarkingResponse>(
                            `/api/v1/customers/${customerId}/reports/benchmarking`,
                            { params: sanitizedParams }
                        )
                    options.onSuccess?.(response.data)
                    return response.data
                } catch (error) {
                    options.onError?.()
                    throw error
                }
            },
            enabled:
                options.enabled &&
                !!accessToken &&
                !!customerId &&
                !!sanitizedParams?.['nodeIds[]'] &&
                !!sanitizedParams.commodityTypeId,
            placeholderData: (prev) => prev,
        })

        const downloadXlsx = useCallback(async () => {
            const response = await axiosInstance.get<Blob>(
                `/api/v1/customers/${customerId}/reports/benchmarking`,
                {
                    params,
                    headers: {
                        Accept: 'application/vnd.ms-excel',
                    },
                    responseType: 'blob',
                }
            )

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'benchmarking.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }, [params])

        return { ...query, downloadXlsx }
    }
    return {
        useUsageCostQuery,
        useInvoicesSummaryQuery,
        useBenchmarkingQuery,
    }
}

type useUsageCostQueryOpts = {
    onSuccess?: (data: UsageCostResponseBody) => void
    onError?: () => void
    enabled?: boolean
}

type useBenchmarkingQueryOpts = {
    enabled?: boolean
    onSuccess?: (data: BenchmarkingResponse) => void
    onError?: () => void
}
